
import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Shield, Lock, Award, Eye } from "lucide-react";

const New2Security = () => {
  const { language } = useLanguage();
  
  const content = {
    fr: {
      title: "Sécurité et confiance",
      subtitle: "Vos données et investissements protégés selon les plus hauts standards",
      features: [
        {
          icon: Shield,
          title: "Standards bancaires européens",
          description: "Nous appliquons les mêmes protocoles de sécurité que les banques européennes agréées ACPR pour protéger vos données et investissements."
        },
        {
          icon: Lock,
          title: "Chiffrement de bout en bout",
          description: "Tous vos messages, photos et vidéos sont chiffrés avec les technologies les plus avancées. Seuls les destinataires pourront les consulter."
        },
        {
          icon: Award,
          title: "Certifications européennes",
          description: "Nous respectons le RGPD et toutes les réglementations financières européennes. Vos droits et votre vie privée sont notre priorité."
        },
        {
          icon: Eye,
          title: "Transparence totale",
          description: "Accédez à tout moment à vos investissements, consultez les performances et modifiez vos capsules en toute liberté."
        }
      ]
    },
    en: {
      title: "Security and trust",
      subtitle: "Your data and investments protected to the highest standards",
      features: [
        {
          icon: Shield,
          title: "European banking standards",
          description: "We apply the same security protocols as ACPR-regulated European banks to protect your data and investments."
        },
        {
          icon: Lock,
          title: "End-to-end encryption",
          description: "All your messages, photos and videos are encrypted with the most advanced technologies. Only recipients will be able to view them."
        },
        {
          icon: Award,
          title: "European certifications",
          description: "We comply with GDPR and all European financial regulations. Your rights and privacy are our priority."
        },
        {
          icon: Eye,
          title: "Total transparency",
          description: "Access your investments at any time, check performance and modify your capsules freely."
        }
      ]
    },
    de: {
      title: "Sicherheit und Vertrauen",
      subtitle: "Ihre Daten und Investitionen nach höchsten Standards geschützt",
      features: [
        {
          icon: Shield,
          title: "Europäische Bankenstandards",
          description: "Wir wenden dieselben Sicherheitsprotokolle an wie die ACPR-regulierten europäischen Banken, um Ihre Daten und Investitionen zu schützen."
        },
        {
          icon: Lock,
          title: "Ende-zu-Ende-Verschlüsselung",
          description: "Alle Ihre Nachrichten, Fotos und Videos werden mit den fortschrittlichsten Technologien verschlüsselt. Nur Empfänger können sie einsehen."
        },
        {
          icon: Award,
          title: "Europäische Zertifizierungen",
          description: "Wir befolgen die DSGVO und alle europäischen Finanzvorschriften. Ihre Rechte und Privatsphäre sind unsere Priorität."
        },
        {
          icon: Eye,
          title: "Vollständige Transparenz",
          description: "Greifen Sie jederzeit auf Ihre Investitionen zu, überprüfen Sie die Leistung und ändern Sie Ihre Kapseln frei."
        }
      ]
    }
  };

  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {content[language].title}
          </h2>
          <p className="text-xl text-gray-600">
            {content[language].subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {content[language].features.map((feature, index) => (
            <div key={index} className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300">
              <div className="flex items-start space-x-4">
                <div className="bg-legatia-blue/10 p-3 rounded-full flex-shrink-0">
                  <feature.icon className="h-6 w-6 text-legatia-blue" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trust badges */}
        <div className="flex justify-center items-center space-x-8 mt-16">
          <div className="flex items-center gap-6 px-8 py-4 bg-white/80 backdrop-blur-sm rounded-full shadow-lg">
            <div className="text-sm font-semibold text-gray-500 flex items-center gap-4">
              <span className="flex items-center gap-2"><Shield className="h-5 w-5 text-primary" /> Agréé ACPR</span>
              <span className="flex items-center gap-2"><Shield className="h-5 w-5 text-primary" /> Conforme AMF</span>
              <span className="flex items-center gap-2"><Shield className="h-5 w-5 text-primary" /> Garanti FGDR</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default New2Security;
